import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Shield, Eye, AlertTriangle, Users, Zap, 
  CheckCircle, ArrowRight, MessageCircle, 
  UserX, Scale, Lock, Bell, FileCheck
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { DemoRequestForm } from '../components/DemoRequestForm';

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

const staggerContainer = {
  animate: { transition: { staggerChildren: 0.1 } }
};

const problems = [
  {
    icon: MessageCircle,
    title: 'Online Trolling & Abuse',
    description: 'Hateful comments and coordinated attacks that damage your mental health and brand image.',
  },
  {
    icon: UserX,
    title: 'Fake Accounts & Impersonation',
    description: 'Scammers pretending to be you, deceiving your followers and stealing opportunities.',
  },
  {
    icon: Scale,
    title: 'Defamation & Legal Threats',
    description: 'False accusations that can harm your reputation and lead to expensive legal battles.',
  },
  {
    icon: Lock,
    title: 'Account Takeovers',
    description: 'Hackers hijacking your accounts, locking you out of your own digital presence.',
  },
];

const howItWorks = [
  {
    step: '01',
    icon: Eye,
    title: '24/7 Monitoring',
    description: 'We scan mentions, tags, and impersonation attempts across all major platforms.',
  },
  {
    step: '02',
    icon: Bell,
    title: 'Real-Time Alerts',
    description: 'Get instant notifications when we detect threats or suspicious activity.',
  },
  {
    step: '03',
    icon: Shield,
    title: 'Rapid Response',
    description: 'Our team helps you take down fake accounts and report abuse quickly.',
  },
  {
    step: '04',
    icon: FileCheck,
    title: 'Insurance Backup',
    description: 'Financial protection for legal costs and income loss from cyber incidents.',
  },
];

const stats = [
  { value: '10K+', label: 'Creators Protected' },
  { value: '24/7', label: 'Monitoring Active' },
  { value: '< 2hr', label: 'Response Time' },
  { value: '₹10L+', label: 'Coverage Available' },
];

const monthlyFeatures = [
  '24/7 Social Monitoring',
  'Real-time Threat Alerts',
  'Impersonation Detection',
  'Basic Insurance Coverage'
];

const quarterlyFeatures = [
  'Priority Support',
  'Extended Insurance Coverage',
  'Legal Consultation (1hr/quarter)',
  'Dedicated Account Manager'
];

export const Home = () => {
  return (
    <div className="min-h-screen bg-slate-950">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-20" data-testid="hero-section">
        {/* Background glow */}
        <div className="absolute inset-0 hero-glow pointer-events-none" />
        
        <div className="container-custom relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left Content */}
            <motion.div 
              className="text-center lg:text-left"
              initial="initial"
              animate="animate"
              variants={staggerContainer}
            >
              <motion.div 
                variants={fadeInUp}
                className="inline-flex items-center gap-2 bg-cyan-400/10 border border-cyan-400/20 rounded-full px-4 py-2 mb-6"
              >
                <Shield className="w-4 h-4 text-cyan-400" />
                <span className="text-cyan-400 text-sm font-medium">India's First Creator Protection Platform</span>
              </motion.div>

              <motion.h1 
                variants={fadeInUp}
                className="text-hero text-white mb-6"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                Protect Your <span className="text-cyan-400">Digital Reputation</span>
              </motion.h1>

              <motion.p 
                variants={fadeInUp}
                className="text-body max-w-xl mx-auto lg:mx-0 mb-8"
              >
                Real-time monitoring + insurance coverage for influencers. 
                Shield your brand from trolls, impersonators, and online threats.
              </motion.p>

              <motion.div 
                variants={fadeInUp}
                className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start"
              >
                <Link to="/contact">
                  <Button 
                    className="w-full sm:w-auto bg-cyan-400 text-slate-950 hover:bg-cyan-300 rounded-full px-8 py-6 font-bold text-base transition-all hover:scale-105 hover:shadow-[0_0_20px_rgba(34,211,238,0.4)]"
                    data-testid="hero-cta-primary"
                  >
                    Get Protected Now
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <Link to="/how-it-works">
                  <Button 
                    variant="outline"
                    className="w-full sm:w-auto bg-slate-800/50 text-white border border-slate-700 hover:border-cyan-400/50 hover:bg-slate-800 rounded-full px-8 py-6 font-medium transition-all backdrop-blur-sm"
                    data-testid="hero-cta-secondary"
                  >
                    See How It Works
                  </Button>
                </Link>
              </motion.div>

              {/* Stats */}
              <motion.div 
                variants={fadeInUp}
                className="grid grid-cols-2 sm:grid-cols-4 gap-6 mt-12 pt-12 border-t border-slate-800"
              >
                {stats.map((stat, index) => (
                  <div key={index} className="text-center lg:text-left">
                    <div className="text-2xl md:text-3xl font-bold text-cyan-400" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      {stat.value}
                    </div>
                    <div className="text-sm text-slate-500">{stat.label}</div>
                  </div>
                ))}
              </motion.div>
            </motion.div>

            {/* Right - Image/Visual */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative hidden lg:block"
            >
              <div className="relative">
                {/* Main image container */}
                <div className="relative rounded-3xl overflow-hidden border border-slate-800 glow">
                  <img
                    src="https://images.unsplash.com/photo-1718874380569-7a44071f01c3?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
                    alt="Creator recording content"
                    className="w-full h-auto object-cover"
                  />
                  {/* Overlay gradient */}
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 to-transparent" />
                </div>

                {/* Floating badge */}
                <div className="absolute -bottom-4 -left-4 bg-slate-900/90 backdrop-blur-xl border border-cyan-400/30 rounded-2xl p-4 animate-float">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-cyan-400/20 flex items-center justify-center">
                      <Shield className="w-6 h-6 text-cyan-400" />
                    </div>
                    <div>
                      <div className="text-sm font-bold text-white">Protected</div>
                      <div className="text-xs text-slate-400">Real-time monitoring active</div>
                    </div>
                  </div>
                </div>

                {/* Alert badge */}
                <div className="absolute -top-4 -right-4 bg-slate-900/90 backdrop-blur-xl border border-slate-700 rounded-2xl p-4 animate-float" style={{ animationDelay: '1s' }}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                      <CheckCircle className="w-5 h-5 text-green-400" />
                    </div>
                    <div>
                      <div className="text-sm font-bold text-white">All Clear</div>
                      <div className="text-xs text-slate-400">No threats detected</div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Problems Section */}
      <section className="section-spacing bg-slate-950 relative" data-testid="problems-section">
        <div className="container-custom">
          <motion.div 
            className="text-center max-w-3xl mx-auto mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-section text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              The Digital Threats <span className="text-cyan-400">Creators Face</span>
            </h2>
            <p className="text-body">
              Growing online means growing risks. One viral attack can destroy years of hard work.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {problems.map((problem, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="card-base group"
              >
                <div className="flex items-start gap-6">
                  <div className="w-14 h-14 rounded-2xl bg-red-500/10 flex items-center justify-center flex-shrink-0 group-hover:bg-red-500/20 transition-colors">
                    <problem.icon className="w-7 h-7 text-red-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      {problem.title}
                    </h3>
                    <p className="text-slate-400 leading-relaxed">
                      {problem.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Preview */}
      <section className="section-spacing bg-slate-900/50 relative" data-testid="how-it-works-preview">
        <div className="container-custom">
          <motion.div 
            className="text-center max-w-3xl mx-auto mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-section text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              How <span className="text-cyan-400">Sozialsetu</span> Protects You
            </h2>
            <p className="text-body">
              A comprehensive shield that monitors, alerts, responds, and insures.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {howItWorks.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="card-base text-center group"
              >
                <div className="text-6xl font-bold text-slate-800 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {item.step}
                </div>
                <div className="w-16 h-16 rounded-2xl bg-cyan-400/10 flex items-center justify-center mx-auto mb-6 group-hover:bg-cyan-400/20 transition-colors">
                  <item.icon className="w-8 h-8 text-cyan-400" />
                </div>
                <h3 className="text-lg font-bold text-white mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {item.title}
                </h3>
                <p className="text-slate-400 text-sm leading-relaxed">
                  {item.description}
                </p>
              </motion.div>
            ))}
          </div>

          <motion.div 
            className="text-center mt-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Link to="/how-it-works">
              <Button 
                variant="outline"
                className="bg-slate-800/50 text-white border border-slate-700 hover:border-cyan-400/50 hover:bg-slate-800 rounded-full px-8 py-4 font-medium transition-all"
                data-testid="learn-more-cta"
              >
                Learn More About Our Process
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Pricing Preview */}
      <section className="section-spacing bg-slate-950 relative" data-testid="pricing-preview">
        <div className="container-custom">
          <motion.div 
            className="text-center max-w-3xl mx-auto mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-section text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Simple, <span className="text-cyan-400">Transparent</span> Pricing
            </h2>
            <p className="text-body">
              Affordable protection plans designed for Indian creators.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Monthly Plan */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="card-base"
            >
              <div className="text-center">
                <h3 className="text-xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Monthly Plan
                </h3>
                <p className="text-slate-400 mb-6">Perfect for getting started</p>
                <div className="mb-6">
                  <span className="text-5xl font-bold text-cyan-400" style={{ fontFamily: 'Outfit, sans-serif' }}>₹2,000</span>
                  <span className="text-slate-400">/month</span>
                </div>
                <ul className="space-y-3 mb-8 text-left">
                  {monthlyFeatures.map((feature, i) => (
                    <li key={i} className="flex items-center gap-3 text-slate-300">
                      <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/pricing">
                  <Button 
                    variant="outline"
                    className="w-full bg-slate-800/50 text-white border border-slate-700 hover:border-cyan-400/50 hover:bg-slate-800 rounded-full py-4 font-medium transition-all"
                    data-testid="pricing-monthly-cta"
                  >
                    Get Started
                  </Button>
                </Link>
              </div>
            </motion.div>

            {/* Quarterly Plan */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="card-base border-cyan-400/30 relative"
            >
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-cyan-400 text-slate-950 px-4 py-1 rounded-full text-sm font-bold">
                BEST VALUE
              </div>
              <div className="text-center">
                <h3 className="text-xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Quarterly Plan
                </h3>
                <p className="text-slate-400 mb-6">Save ₹1,000 every quarter</p>
                <div className="mb-6">
                  <span className="text-5xl font-bold text-cyan-400" style={{ fontFamily: 'Outfit, sans-serif' }}>₹5,000</span>
                  <span className="text-slate-400">/quarter</span>
                </div>
                <ul className="space-y-3 mb-8 text-left">
                  {quarterlyFeatures.map((feature, i) => (
                    <li key={i} className="flex items-center gap-3 text-slate-300">
                      <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/pricing">
                  <Button 
                    className="w-full bg-cyan-400 text-slate-950 hover:bg-cyan-300 rounded-full py-4 font-bold transition-all hover:scale-[1.02]"
                    data-testid="pricing-quarterly-cta"
                  >
                    Get Best Value
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>

          <motion.div 
            className="text-center mt-8"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Link to="/pricing" className="text-cyan-400 hover:text-cyan-300 transition-colors font-medium">
              View Full Pricing Details <ArrowRight className="w-4 h-4 inline ml-1" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-spacing bg-slate-900/50 relative overflow-hidden" data-testid="cta-section">
        {/* Background glow */}
        <div className="absolute inset-0 hero-glow pointer-events-none" />
        
        <div className="container-custom relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-section text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Ready to <span className="text-cyan-400">Protect</span> Your Digital Identity?
              </h2>
              <p className="text-body mb-8">
                Join thousands of Indian creators who trust Sozialsetu to safeguard their online presence. 
                Get started in minutes.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="https://wa.me/919986573456"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 bg-green-600 hover:bg-green-500 text-white px-6 py-4 rounded-full font-bold transition-all hover:scale-105"
                  data-testid="whatsapp-cta"
                >
                  <MessageCircle className="w-5 h-5" />
                  Chat on WhatsApp
                </a>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-slate-900/80 backdrop-blur-xl border border-slate-800 rounded-3xl p-8"
            >
              <h3 className="text-2xl font-bold text-white mb-6 text-center" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Request a Demo
              </h3>
              <DemoRequestForm />
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};
